import { ReactNode } from "react";
import { Head, Footer, DefaultThemeProvider } from "@dreambigger/design-system";
import { Header, Menu, MenuItem, Toggle } from "../ui";

export interface AppFrameHead {
  title: string;
  description?: string;
  siteName?: string;
}

/**
 * Shared application shell for dreambigger Next.js apps: `<head>` metadata,
 * theme, top header (with cross-app nav + user menu), an optional left sidebar
 * menu, and the footer — the chrome every app reproduces by hand today (see
 * boa-settings `settings-frame.tsx`).
 *
 * Data/auth-AGNOSTIC: the consuming app resolves the org, the active user, the
 * nav items, and theming, then passes them in. New apps get the standard frame
 * without copy-paste; existing apps are intentionally left on their own frames.
 */
export interface AppFrameProps {
  /** Top-bar cross-app navigation links (e.g. Portal, Settings, Academy). */
  navItems?: MenuItem[];
  /** Left-sidebar (page-specific) menu items; the sidebar is hidden when empty. */
  menuItems?: MenuItem[];
  /** Sidebar heading. */
  sidebarTitle?: string;
  /** Slug/id of the active sidebar item (for highlight). */
  activeItem?: string;
  /** Authenticated user + the user-dropdown actions (logout, reset password…). */
  activeUser?: any;
  userItems?: MenuItem[];
  /** Feature toggles rendered in the header (e.g. Sandbox mode). */
  toggles?: Toggle[];
  logo?: string;
  helpItem?: MenuItem;
  /** `<head>` metadata; omit to let the page own its head. */
  head?: AppFrameHead;
  /** Footer config; falls back to the design-system default footer. */
  footer?: Record<string, any>;
  /** Theme color overrides passed to `DefaultThemeProvider`. */
  colors?: any;
  children?: ReactNode;
}

export default function AppFrame({
  navItems,
  menuItems,
  sidebarTitle,
  activeItem,
  activeUser,
  userItems,
  toggles,
  logo,
  helpItem,
  head,
  footer,
  colors,
  children,
}: AppFrameProps) {
  const hasSidebar = !!menuItems?.length;

  return (
    <div className="flex flex-column min-h-100vh">
      {head && (
        <Head
          title={head.title}
          description={head.description}
          siteName={head.siteName}
        />
      )}
      <DefaultThemeProvider colors={colors}>
        <div className="flex flex-column min-h-100vh">
          <Header
            navItems={navItems}
            menuItems={menuItems}
            title={sidebarTitle}
            activeUser={activeUser}
            userItems={userItems}
            toggles={toggles}
            logo={logo}
            helpItem={helpItem}
          />
          {/* --- BODY --- */}
          <div className="flex flex-grow-1">
            {/* Sidebar (desktop); on mobile the header's hamburger surfaces menuItems. */}
            {hasSidebar && (
              <div className="dn md_db" style={{ flex: "0 0 240px" }}>
                <Menu
                  className="h-100 bg-white"
                  title={sidebarTitle}
                  items={menuItems}
                  activeItem={activeItem}
                />
              </div>
            )}
            {/* CONTENT */}
            <div className="w-100 bg-gray-2">{children}</div>
          </div>
          {footer ? <Footer {...(footer as any)} /> : null}
        </div>
      </DefaultThemeProvider>
    </div>
  );
}
